import {
  Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { FC, memo, useEffect, useMemo, useState } from 'react';
import { DeleteConfirmDialog } from 'src/components/DeleteConfirmDialog';
import {
  DeletionCandidateEntity,
  useImportDeletionCandidatesMutation,
  useImportDeletionCandidatesQuery,
} from 'src/hooks/useImportDeletionCandidates.query';

type Props = {
  open: boolean;
  importId: number;
  importedAt: string | null;
  ruleName: string | null;
  onClose: () => void;
};

const formatImportedAt = (importedAt: string | null) => (
  importedAt ? format(new Date(importedAt), 'M/d HH:mm') : ''
);

/**
 * 取込ファイルに無かった案件を出し、削除するものを選ばせる（#405 決定 6）。
 *
 * Why not 既定で全件チェック: 決定 6 は「自動削除しない」なので、押し間違いで
 * 全件消えるのを既定の状態にしない。
 * Why not 「あとで」: 未処理のまま忘れられるのが一番困るので、閉じても候補は残り続ける。
 * すぐ片付けたくないときは「すべて残す」で消す
 * Why 開くたびに選択を空にする: 閉じた選択が生き残ると、消す気が変わって閉じたあとに
 * 開き直して押しただけで前の選択ぶんが消える
 */
export const DeletionCandidatesDialog: FC<Props> = memo(({ open, importId, importedAt, ruleName, onClose }) => {
  const candidatesQuery = useImportDeletionCandidatesQuery(open ? importId : null);
  const resolve = useImportDeletionCandidatesMutation();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [confirming, setConfirming] = useState(false);

  // 外側クリックや Esc で閉じると finish() を通らないので、開くたびに選び直しにする。
  // Why not 閉じるときに消す: 削除の確認ダイアログを「キャンセル」したときは
  // 選択が残っていてほしく、どちらも onClose を通らないので開く側で揃える
  useEffect(() => {
    if (open) setSelectedIds([]);
  }, [open]);

  const candidates = useMemo(
    () => (candidatesQuery.data ?? []).filter((it) => it.status === 'pending'),
    [candidatesQuery.data],
  );
  const selected = useMemo(
    () => candidates.filter((it) => selectedIds.includes(it.shipmentOrderId)),
    [candidates, selectedIds],
  );
  const allocatedSelectedCount = selected.filter((it) => it.allocated).length;

  const toggle = (candidate: DeletionCandidateEntity) => {
    setSelectedIds((prev) => (
      prev.includes(candidate.shipmentOrderId)
        ? prev.filter((id) => id !== candidate.shipmentOrderId)
        : [...prev, candidate.shipmentOrderId]
    ));
  };

  const finish = () => {
    setSelectedIds([]);
    setConfirming(false);
    onClose();
  };

  const deleteSelected = () => {
    resolve.mutate(
      { importId, deleteIds: selectedIds, keepIds: [] },
      { onSuccess: finish },
    );
  };

  const keepAll = () => {
    resolve.mutate(
      { importId, deleteIds: [], keepIds: candidates.map((it) => it.shipmentOrderId) },
      { onSuccess: finish },
    );
  };

  const confirmMessage = [
    `選んだ ${selected.length} 件の案件を削除します。`,
    allocatedSelectedCount > 0
      ? `配車済みの案件が ${allocatedSelectedCount} 件含まれます。削除すると配車もなくなります。`
      : null,
  ].filter(Boolean).join('\n');

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>取込ファイルに無い案件</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {`${formatImportedAt(importedAt)} の取込${ruleName ? `（${ruleName}）` : ''}に含まれていなかった案件です。削除するものを選んでください。`}
          </Typography>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>伝票番号</TableCell>
                  <TableCell>降地</TableCell>
                  <TableCell>積込日</TableCell>
                  <TableCell>配車</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {candidates.map((candidate) => (
                  <TableRow key={candidate.shipmentOrderId} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(candidate.shipmentOrderId)}
                        onChange={() => toggle(candidate)}
                        inputProps={{ 'aria-label': `${candidate.code} を削除する` }}
                      />
                    </TableCell>
                    <TableCell>{candidate.code}</TableCell>
                    <TableCell>{candidate.unloadingName}</TableCell>
                    <TableCell>{candidate.loadingOn}</TableCell>
                    <TableCell>
                      {candidate.allocated ? <Chip size="small" color="error" label="配車済み" /> : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={keepAll} disabled={resolve.isPending}>すべて残す</Button>
          <Button
            color="error"
            variant="contained"
            disabled={selected.length === 0 || resolve.isPending}
            onClick={() => setConfirming(true)}
          >
            {`選んだ案件を削除する（${selected.length} 件）`}
          </Button>
        </DialogActions>
      </Dialog>
      <DeleteConfirmDialog
        open={confirming}
        message={confirmMessage}
        okLabel="削除する"
        onOk={deleteSelected}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
});
